import { usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import CN from 'classnames'

import { Button } from 'components/atoms/Button'

const Table = ({
  columns,
  data,
  selectedTableRows,
  handleInputChange,
  handleButtonClick,
  handleButtonCloseClick,
  partnerOrderStatus,
  deleteOrderLinePopOut,
  setDeleteOrderLinePopOut,
  partnerCode,
  isInternationalOrder,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    toggleAllRowsSelected,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 30,
        pageIndex: 0,
        selectedRowIds: selectedTableRows || [],
      },
      manualPagination: true,
      manualSortBy: false,
      autoResetPage: false,
      autoResetSelectedRows: false,
      disableSortBy: false,
    },
    useSortBy,
    usePagination,
    useRowSelect,
  )

  const slicedPageData = page

  return (
    <div className='styled-scroll h-[calc(100%-60px)] relative overflow-y-auto rounded-t-md border border-N-200'>
      <table className='w-full overflow-y-auto' {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={CN(
                    'bg-N-100 border-b border-b-N-200 sticky top-0 shadow-sm text-left pl-4 z-[1]',
                    { 'bg-N-200': column.isSorted },
                  )}
                  style={{ height: 40 }}
                  width={column.customWidth}>
                  <div
                    className='inline-flex items-center'
                    style={{ minHeight: 20 }}>
                    {column.render('Header')}
                    <span className='flex ml-1' style={{ width: 14 }}>
                      {column.isSorted &&
                        (column.isSortedDesc ? (
                          <i className='ri-arrow-up-s-line' />
                        ) : (
                          <i className='ri-arrow-down-s-line' />
                        ))}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {slicedPageData.map((row) => {
            prepareRow(row)
            return (
              <tr
                {...row.getRowProps()}
                className={CN('h-11 cursor-pointer', {
                  'border-l-8 border-l-R-500':
                    row.original.wareHouseOrderLineError,
                })}
                onClick={() => {
                  toggleAllRowsSelected(false)
                  row.toggleRowSelected()
                }}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    className={CN(
                      'border-b border-b-N-200 text-sm text-N-700 py-[11px] px-4',
                      { 'bg-N-300 border-b border-b-N-400': cell.isSelected },
                    )}>
                    {/* ===================== EDITABLE UNIT PRICE ===================== */}
                    {cell.column.isUnitPriceEditable ? (
                      row.original.showEditView ? (
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          value={row.original.totalPrice}
                          onChange={(e) => {
                            const val = e.target.value
                            if (parseFloat(val) >= 0 || val === '') {
                              handleInputChange(row.original, val, 'totalPrice')
                            }
                          }}
                          className='text-sm font-normal text-N-700 px-3 py-[6px] border border-N-200 rounded-md w-24 outline-none active:border-N-300'
                        />
                      ) : (
                        <div>
                          {parseFloat(row.original.totalPrice || 0).toFixed(2)}
                        </div>
                      )
                    ) : /* ===================== UNIT PRICE ACTION BUTTONS ===================== */
                    cell.column.isUnitPriceAction ? (
                      partnerOrderStatus === 'ONHOLD' &&
                      partnerCode === 'UM' &&
                      isInternationalOrder &&
                      (row.original.showEditView ? (
                        <div className='flex items-center gap-2'>
                          {/* Save */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleButtonClick(
                                data.find((d) => d.id === row.original.id),
                                'update',
                              )
                            }}
                            disabled={
                              row.original.showEditView &&
                              (row.original.totalPrice === '' ||
                                parseFloat(row.original.totalPrice) < 0)
                            }
                            className='bg-G-500 text-white text-sm px-3 py-[3px] rounded-md font-medium'>
                            Save
                          </button>

                          {/* Cancel */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleButtonCloseClick(row.original)
                            }}
                            className='text-sm px-3 py-[3px] border border-N-300 rounded-md text-N-700 bg-white hover:bg-N-100'>
                            <i className='ri-close-line'></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleButtonClick(row.original, 'edit')
                          }}>
                          <i className='ri-edit-line' />
                        </button>
                      ))
                    ) : /* ===================== WAREHOUSE SKU (EDIT / UPDATE / DELETE) ===================== */
                    cell.column.isWarehouseSKU ? (
                      <div className='flex items-center w-fit'>
                        {row.original.showEditView ? (
                          <input
                            type='text'
                            value={row.original.warehouseSKU}
                            onChange={(e) => {
                              // use the same handler signature as Unit Price for consistency
                              handleInputChange(
                                row.original,
                                e.target.value,
                                'warehouseSKU',
                              )
                            }}
                            className='text-sm font-normal text-N-700 px-3 py-[6px] border border-N-200 rounded-md w-24 outline-none active:border-N-300'
                          />
                        ) : (
                          <p className='text-sm font-normal text-N-700'>
                            {row.original.warehouseSKU}
                          </p>
                        )}

                        {/* Edit/Save toggle for RECEIVED / FAILED / ERRORED */}
                        {(partnerOrderStatus === 'RECEIVED' ||
                          partnerOrderStatus === 'FAILED' ||
                          partnerOrderStatus === 'ERRORED') && (
                          <Button
                            disabled={
                              row.original.showEditView &&
                              row.original.warehouseSKU.trim().length === 0
                            }
                            className='ml-1'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleButtonClick(
                                row.original,
                                row.original.showEditView ? 'update' : 'edit',
                              )
                            }}
                            appearance='dull'
                            size='xs'>
                            <i
                              className={CN({
                                'ri-edit-line': !row.original.showEditView,
                                'ri-save-line': row.original.showEditView,
                              })}
                            />
                          </Button>
                        )}

                        {/* Delete only if FAILED/ERRORED and more than 1 line */}
                        {(partnerOrderStatus === 'FAILED' ||
                          partnerOrderStatus === 'ERRORED') &&
                          data?.length > 1 && (
                            <Button
                              className='ml-1'
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteOrderLinePopOut({
                                  ...deleteOrderLinePopOut,
                                  enable: true,
                                  orderLineId: row.original?.id,
                                })
                              }}
                              appearance='dull'
                              size='xs'>
                              <i className='ri-delete-bin-line' />
                            </Button>
                          )}

                        {/* Close (cancel edit) visible only while editing */}
                        {row.original.showEditView && (
                          <Button
                            className='ml-1'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleButtonCloseClick(row.original)
                            }}
                            appearance='dull'
                            size='xs'>
                            <i className='ri-close-line' />
                          </Button>
                        )}
                      </div>
                    ) : (
                      cell.render('Cell')
                    )}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default Table
